import { useAppStore } from "@/global/store";
import React from "react";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { Alert, Box, Grid, Stack } from "@mui/material";
import { selectUser } from "@/global/store/data/user";
import { selectPackageSelected } from "@/global/store/data/purchaseSeats/packages";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { useGoogleAnalytics } from "@/global/hooks/useGoogleAnalytics";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";
import { inventoryTypeUIDs } from "@/global/config/constants";
import { AnimatedTextPreloader } from "@/shared/common/preloader/AnimatedTextPreloader";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";

interface CartErrorProps {
  isVisible: boolean;
  errorMessage?: string;
}
export function CartError({ isVisible, errorMessage }: CartErrorProps) {
  return (
    <Box
      sx={{
        height: isVisible ? "70px" : 0,
        transition: "all 0.3s ease-in-out",
        // transform: isVisible ? "scaleY(1)" : "scaleY(0)",
        transformOrigin: "top",
        paddingBottom: isVisible ? 2 : 0,
        opacity: isVisible ? 1 : 0,
      }}
    >
      <Alert severity="warning">{errorMessage}</Alert>
    </Box>
  );
}

export function AdaInfoMessage(props: { isVisible: boolean }) {
  return (
    <Box
      sx={{
        height: props?.isVisible ? "90px" : 0,
        transition: "all 0.3s ease-in-out",
        // transform: isVisible ? "scaleY(1)" : "scaleY(0)",
        transformOrigin: "top",
        paddingBottom: props?.isVisible ? 2 : 0,
        opacity: props?.isVisible ? 1 : 0,
      }}
    >
      <Alert severity="info">
        Please select all individual seats. To purchase a Companion seat, you
        must also select a Wheelchair seat.
      </Alert>
    </Box>
  );
}

export function useChooseSeatsAnalytics() {
  const analyticsRef = React.useRef({ isFired: false });
  const user = useAppStore(selectUser);
  const selectedPackage = useAppStore(selectPackageSelected);
  const selectedEvents = useAppStore(selectEventsSelected);
  const { fireAnalyticsPkgSelect, fireAnalyticsEventSelect } = useGoogleAnalytics(); // prettier-ignore

  // Handle analytics
  React.useEffect(() => {
    if (!analyticsRef.current.isFired && user) {
      analyticsRef.current.isFired = true;
      if (selectedPackage) fireAnalyticsPkgSelect(selectedPackage);
      if (selectedEvents) fireAnalyticsEventSelect(selectedEvents);
    }
  }, [selectedEvents, selectedPackage, user]);
}

export function InventoryEmptyState() {
  const teamConfig = useTeamConfig();

  return (
    <Stack
      width={"100%"}
      height="100%"
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Box>
        <EmptyState
          title={
            teamConfig?.uiConfigs?.inventoryContainer?.emptyStateTitle ||
            "No seats available"
          }
          description={
            teamConfig?.uiConfigs?.inventoryContainer?.emptyStateDesc ||
            "Try adjusting your filters."
          }
        />
      </Box>
    </Stack>
  );
}

export const InventoryLoadingState = React.memo(() => {
  const { md: mdDown } = useCssMediaQuery("down");
  const { md: mdUp } = useCssMediaQuery("up");
  const teamConfig = useTeamConfig();
  const customMessages = React.useMemo(
    () => teamConfig?.uiConfigs?.chooseSeatsPreloaderTexts,
    []
  );

  if (teamConfig?.inventoryType === inventoryTypeUIDs.groupLevel) {
    return (
      <Stack
        width={"100%"}
        height={mdUp ? "100%" : "80px"}
        justifyContent={"center"}
        alignItems={"center"}
        py={mdDown ? 12 : 0}
      >
        <Box width={"80%"}>
          <AnimatedTextPreloader messages={customMessages} />
        </Box>
      </Stack>
    );
  }

  return (
    <Box width={"100%"}>
      <Grid container spacing={4}>
        {Array.from({ length: mdDown ? 3 : 6 })
          .fill(1)
          .map((itm, idx) => {
            return (
              <Grid key={idx} item xs={12} sm={12} lg={6}>
                <DynamicSkeleton
                  rowCount={3}
                  spacing={2}
                  widths={["100%", "80%", "60%"]}
                  heights={["18px", "18px", "18px"]}
                />
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
});

InventoryLoadingState.displayName = "InventoryLoadingState";
